import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Switch,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

// Clean, India-appropriate defaults. These are only used until the store saves
// its own custom.languages list (which the seeded DB does not yet contain).
const DEFAULT_LANGUAGES = [
  { id: 1, name: 'English', code: 'en', status: true, defaultStatus: true },
  { id: 2, name: 'Hindi', code: 'hi', status: true, defaultStatus: false },
];

const LanguageSettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [languages, setLanguages] = useState(DEFAULT_LANGUAGES);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const savedLangs = settings.custom?.languages;
    if (Array.isArray(savedLangs) && savedLangs.length) {
      setLanguages(savedLangs);
      return;
    }
    // No saved list yet: align the default language with the system preference.
    const defaultCode = settings.systemPreferences?.language;
    if (defaultCode) {
      setLanguages((prev) =>
        prev.map((l) => ({ ...l, defaultStatus: l.code === defaultCode }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const nextId = () =>
    languages.reduce((max, l) => Math.max(max, Number(l.id) || 0), 0) + 1;

  const handleFieldChange = (index, field, value) => {
    const updated = [...languages];
    updated[index] = { ...updated[index], [field]: value };
    setLanguages(updated);
  };

  const handleStatusChange = (index) => {
    const updated = [...languages];
    updated[index] = { ...updated[index], status: !updated[index].status };
    setLanguages(updated);
  };

  const handleDefaultStatusChange = (index) => {
    // Only one language may be the default.
    setLanguages((prev) =>
      prev.map((lang, i) => ({ ...lang, defaultStatus: i === index }))
    );
  };

  const handleEdit = (id) => {
    setEditingId((current) => (current === id ? null : id));
  };

  const handleDelete = (index) => {
    setLanguages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddLanguage = () => {
    const id = nextId();
    setLanguages((prev) => [
      ...prev,
      { id, name: '', code: '', status: true, defaultStatus: false },
    ]);
    setEditingId(id);
  };

  const handleSave = async () => {
    const defaultLang = languages.find((l) => l.defaultStatus);
    try {
      await updateSettings({
        systemPreferences: defaultLang ? { language: defaultLang.code } : {},
        custom: { languages },
      }).unwrap();
      setEditingId(null);
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid
        item
        xs={12}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="h6">Language Table</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddLanguage}
        >
          Add New Language
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SL</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {languages.map((language, index) => {
              const isEditing = editingId === language.id;
              return (
                <TableRow key={language.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder="Language name"
                        value={language.name}
                        onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      />
                    ) : (
                      language.name || '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder="e.g. en"
                        value={language.code}
                        onChange={(e) => handleFieldChange(index, 'code', e.target.value)}
                      />
                    ) : (
                      language.code || '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!language.status}
                      onChange={() => handleStatusChange(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!language.defaultStatus}
                      onChange={() => handleDefaultStatusChange(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={isEditing ? 'Done' : 'Edit'}>
                      <IconButton onClick={() => handleEdit(language.id)}>
                        {isEditing ? <CheckIcon /> : <EditIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Grid>

      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Grid>

      <SaveSnackbar snack={snack} onClose={closeSnack} />
    </Grid>
  );
};

export default LanguageSettings;
